import React, { useEffect, useMemo, useState } from 'react';
import { Film, MapPin, Wallet, Smartphone, Clock } from 'lucide-react';
import InvoiceLink from './InvoiceLink';
import apiService from '../apiService';
import mmPay from './mmPay';

/**
 * Cinéma web (2026-11-04) — films à l'affiche, fiche film (séances), séance (plan de salle ou nombre de
 * places, tarifs, confiserie, attestation d'âge, paiement solde ou Mobile Money pay-first), billets.
 * Mêmes endpoints que l'app native : /cinema/movies, /cinema/movies/:id, /cinema/showtimes/:id,
 * /cinema/showtimes/:id/order, /cinema/orders/:id/confirm, /cinema/my/tickets.
 */
const fmtTime = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const fmtDay = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const fmtDT = (d) => new Date(d).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const money = (n) => `${Math.round(n || 0).toLocaleString('fr-FR')} FC`;
const ymd = (d) => { const z = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`; };

export function CinemaHome({ nav, citySlug, supplierId }) {
  const [movies, setMovies] = useState(null);
  const [cinemas, setCinemas] = useState([]);
  const [date, setDate] = useState('');
  const chips = useMemo(() => { const out = [{ key: '', label: '7 jours' }]; for (let i = 0; i < 7; i++) { const d = new Date(); d.setDate(d.getDate() + i); out.push({ key: ymd(d), label: i === 0 ? 'Aujourd’hui' : i === 1 ? 'Demain' : d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }) }); } return out; }, []);

  useEffect(() => {
    const q = new URLSearchParams({ ...(citySlug ? { city: citySlug } : {}), ...(date ? { date } : {}) }).toString();
    apiService.get(`/cinema/movies?${q}`).then(r => setMovies((r?.movies || []).filter(m => !supplierId || m.cinemas.some(c => c.supplier_id === supplierId)))).catch(() => setMovies([]));
    apiService.get(`/cinema/cinemas${citySlug ? `?city=${encodeURIComponent(citySlug)}` : ''}`).then(r => setCinemas(r?.cinemas || [])).catch(() => {});
  }, [citySlug, date, supplierId]);

  if (movies === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 8, flexWrap: 'wrap' }}>
        <div className="mk-cats" style={{ margin: 0 }}>
          {chips.map(c => <button key={c.key || 'all'} className={`mk-cat${date === c.key ? ' active' : ''}`} onClick={() => setDate(c.key)}><span>{c.label}</span></button>)}
        </div>
        <button className="mk-btn ghost" onClick={() => nav({ page: 'tickets' })}>🎟️ Mes billets</button>
      </div>

      {!supplierId && cinemas.length > 0 && (
        <section className="mk-section">
          <div className="mk-section-head"><h2>Cinémas</h2></div>
          <div className="mk-row">
            {cinemas.map(c => (
              <button key={c.supplier_id} className="mk-card" onClick={() => nav({ page: 'cinema', supplierId: c.supplier_id })}>
                <div className="mk-card-imgwrap">{c.logo ? <img className="mk-card-img" src={c.logo_thumb || c.logo} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph">🎬</div>}</div>
                <span className="mk-card-title">{c.business_name}</span>
                <span className="mk-meta">{c.upcoming_showtimes} séance(s) · <MapPin size={11} /> {c.address}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mk-section">
        <div className="mk-section-head"><h2>À l’affiche{supplierId ? ` — ${movies[0]?.cinemas.find(c => c.supplier_id === supplierId)?.business_name || ''}` : ''}</h2></div>
        {!movies.length ? <div className="mk-center"><Film size={42} /> Aucun film à l’affiche pour le moment.</div> : (
          <div style={{ display: 'grid', gap: 12 }}>
            {movies.map(m => (
              <button key={m.movie_id} className="mk-panel" style={{ display: 'flex', gap: 14, textAlign: 'left', cursor: 'pointer', padding: 12 }} onClick={() => nav({ page: 'cinema-movie', movieId: m.movie_id })}>
                {m.poster ? <img src={m.poster} alt="" style={{ width: 90, height: 130, objectFit: 'cover', borderRadius: 10, flex: '0 0 auto' }} /> : <div className="mk-card-ph" style={{ width: 90, height: 130, borderRadius: 10, display: 'grid', placeItems: 'center', fontSize: 30 }}>🎬</div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{m.title}</h3>
                  <p className="mk-note" style={{ margin: '4px 0' }}><Clock size={11} /> {m.duration_min} min{m.genres?.length ? ` · ${m.genres.slice(0, 2).join(', ')}` : ''}{m.age_rating ? ` · ${m.age_rating}+` : ''}</p>
                  <p className="mk-note" style={{ margin: '4px 0' }}><MapPin size={11} /> {m.cinemas.map(c => c.business_name).join(' · ')}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {m.cinemas.flatMap(c => c.showtimes).slice(0, 6).map(s => <span key={s.showtime_id} className="mk-eta" style={{ position: 'static', opacity: s.available <= 0 ? 0.5 : 1 }}>{fmtTime(s.starts_at)}</span>)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function CinemaMovie({ movieId, nav }) {
  const [data, setData] = useState(null);
  useEffect(() => { apiService.get(`/cinema/movies/${movieId}`).then(r => setData(r?.success ? r : { error: true })).catch(() => setData({ error: true })); }, [movieId]);
  if (!data) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  if (data.error) return <div className="mk-center">Film introuvable.</div>;
  const { movie: m, cinema: c, showtimes } = data;
  const days = []; const map = new Map();
  for (const s of showtimes || []) { const k = new Date(s.starts_at).toDateString(); if (!map.has(k)) { map.set(k, { title: fmtDay(s.starts_at), list: [] }); days.push(map.get(k)); } map.get(k).list.push(s); }
  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {m.poster ? <img src={m.poster} alt="" style={{ width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' }} /> : null}
          <div style={{ padding: 18 }}>
            <h3 style={{ fontSize: '1.3rem' }}>{m.title}</h3>
            <p className="mk-note">⏱ {m.duration_min} min{m.age_rating ? ` · ${m.age_rating}+` : ''}{m.formats?.length ? ` · ${m.formats.join(' / ')}` : ''}{m.genres?.length ? ` · ${m.genres.join(', ')}` : ''}</p>
            {(m.language || m.subtitles) && <p className="mk-note">{m.language}{m.subtitles ? ` · sous-titres ${m.subtitles}` : ''}</p>}
            {m.synopsis && <p className="mk-desc" style={{ marginTop: 10 }}>{m.synopsis}</p>}
            {m.trailer_url && <a className="mk-btn ghost" href={m.trailer_url} target="_blank" rel="noreferrer" style={{ marginTop: 10 }}>▶ Bande-annonce</a>}
            {c && <p className="mk-note" style={{ marginTop: 12 }}>🎬 <b>{c.business_name}</b> · {c.address}</p>}
          </div>
        </div>
        <div className="mk-panel">
          <h3>Séances</h3>
          {!days.length && <p className="mk-note">Aucune séance programmée.</p>}
          {days.map(d => (
            <div key={d.title} style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '0.9rem', color: 'var(--mb-text-2, #5C636E)', marginBottom: 6 }}>{d.title}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {d.list.map(s => { const off = s.available <= 0 || !s.online_open; return (
                  <button key={s.showtime_id} className="mk-opt" disabled={off} style={{ width: 'auto', flexDirection: 'column', alignItems: 'flex-start', opacity: off ? 0.55 : 1 }} onClick={() => nav({ page: 'cinema-showtime', showtimeId: s.showtime_id })}>
                    <span className="name" style={{ fontSize: '1.05rem', fontWeight: 800 }}>{fmtTime(s.starts_at)}</span>
                    <span className="delta" style={{ fontSize: '0.75rem' }}>{s.screen_name} · {s.format}</span>
                    <span className="delta" style={{ fontSize: '0.75rem', fontWeight: 700 }}>{s.available <= 0 ? 'Complet' : !s.online_open ? 'Vente en ligne terminée' : s.from_price != null ? `dès ${money(s.from_price)}` : ''}</span>
                  </button>
                ); })}
              </div>
            </div>
          ))}
          <p className="mk-note" style={{ marginTop: 10 }}>Billet ni remboursable ni échangeable, sauf annulation de la séance par le cinéma (remboursement sur votre solde Mbiyo).</p>
        </div>
      </div>
    </div>
  );
}

function SeatMap({ layout, sold, held, selected, onToggle }) {
  const soldSet = new Set(sold), heldSet = new Set(held), selSet = new Set(selected);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ width: '70%', height: 6, background: '#CBD5E1', borderRadius: 3, margin: '0 auto 2px' }} /><div style={{ textAlign: 'center', fontSize: 9, letterSpacing: 2, color: '#9AA0A8', marginBottom: 6 }}>ÉCRAN</div>
      <div style={{ display: 'inline-block', minWidth: '100%' }}>
        {(layout?.rows || []).map((row, ri) => (
          <div key={row.label + ri} style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3, justifyContent: 'center' }}>
            <span style={{ width: 16, fontSize: 10, fontWeight: 800, color: '#9AA0A8' }}>{row.label}</span>
            {(row.seats || []).map((seat, si) => {
              if (!seat || !seat.code || seat.type === 'disabled') return <span key={`e${si}`} style={{ width: 26, height: 26 }} />;
              const isSold = soldSet.has(seat.code), isHeld = heldSet.has(seat.code), isSel = selSet.has(seat.code);
              const bg = isSel ? 'var(--mb-fire, #F54119)' : isSold ? '#CBD5E1' : isHeld ? '#FDE68A' : '#fff';
              return (
                <button key={seat.code} type="button" disabled={isSold || isHeld} onClick={() => onToggle(seat)} title={seat.code}
                  style={{ width: 26, height: 26, borderRadius: 7, border: `${seat.type === 'vip' ? 2 : 1}px solid ${seat.type === 'vip' ? '#D97706' : isSel ? 'var(--mb-fire, #F54119)' : '#DCE0E6'}`, background: bg, color: isSel ? '#fff' : isSold ? '#9AA0A8' : '#1E2229', fontSize: 9, fontWeight: 700, cursor: isSold || isHeld ? 'not-allowed' : 'pointer', padding: 0 }}>
                  {seat.type === 'wheelchair' ? '♿' : seat.code.replace(row.label, '')}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="mk-note" style={{ textAlign: 'center', marginTop: 6 }}>Blanc = libre · Orange = choisie · Gris = prise · Jaune = en cours · Bord doré = VIP</p>
    </div>
  );
}

export function CinemaShowtime({ showtimeId, nav, currentUser }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);   // [{ seat_code, tier_code }]
  const [qty, setQty] = useState({});
  const [tier, setTier] = useState(null);
  const [conc, setConc] = useState({});
  const [attested, setAttested] = useState(false);
  const [pay, setPay] = useState('balance');
  const [phone, setPhone] = useState('');
  const [telecom, setTelecom] = useState('AM');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null);
  const [error, setError] = useState(null);

  const load = () => apiService.get(`/cinema/showtimes/${showtimeId}`).then(r => { if (r?.success) { setData(r); const tiers = (r.showtime.grid?.tiers || []).filter(x => !(r.child_blocked && x.code === 'child')); setTier(t => t || tiers[0]?.code || 'adult'); } else setData({ error: true }); }).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, [showtimeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  if (data.error) return <div className="mk-center">Séance introuvable.</div>;
  const s = data.showtime; const numbered = s.mode !== 'free';
  const tiers = (s.grid?.tiers || []).filter(x => !(data.child_blocked && x.code === 'child'));
  const seatType = {}; for (const r of s.layout?.rows || []) for (const x of r.seats || []) if (x?.code) seatType[x.code] = x.type;
  const unit = (tc, sc) => { const tr = tiers.find(x => x.code === tc) || tiers[0]; let p = Number(tr?.price) || 0; if (sc && seatType[sc] === 'vip') p += Number(s.grid?.vip_supplement) || 0; if (String(s.format).toUpperCase() === '3D') p += Number(s.grid?.format_3d_supplement) || 0; return p; };
  const items = numbered ? selected : Object.entries(qty).flatMap(([tier_code, n]) => Array.from({ length: n }, () => ({ tier_code })));
  const concList = (data.concession || []).filter(p => conc[p.product_id] > 0).map(p => ({ product_id: p.product_id, name: p.name, qty: conc[p.product_id], unit: p.price }));
  const total = items.reduce((a, it) => a + unit(it.tier_code, it.seat_code), 0) + concList.reduce((a, c) => a + c.unit * c.qty, 0);
  const needAge = Number(s.movie?.age_rating || 0) > 0;
  const maxSeats = data.settings?.max_seats || 10;
  const canBuy = s.online_open && items.length > 0 && (!needAge || attested) && !!currentUser;

  const toggle = (seat) => setSelected(l => l.some(x => x.seat_code === seat.code) ? l.filter(x => x.seat_code !== seat.code) : l.length >= maxSeats ? l : [...l, { seat_code: seat.code, tier_code: tier }]);

  const buy = async () => {
    setError(null); setBusy(true);
    try {
      const body = { items, concessions: concList.map(c => ({ product_id: c.product_id, qty: c.qty })), payment_method: pay, age_attested: attested };
      const res = await apiService.post(`/cinema/showtimes/${showtimeId}/order`, body);
      if (!res.success) throw new Error(res.error || 'Réservation impossible');
      if (pay === 'balance') { nav({ page: 'tickets' }); return; }
      setPhase(`Places bloquées ${res.hold_minutes || 10} min — confirmez le paiement sur votre téléphone…`);
      const { paymentId } = await mmPay({ userId: currentUser.user_id, amount: res.amount_total, phone, telecom, label: `Cinéma ${s.movie?.title}`, planType: 'cinema', onPhase: setPhase });
      setPhase('Émission de vos billets…');
      const conf = await apiService.post(`/cinema/orders/${res.order_id}/confirm`, { payment_ref: paymentId });
      if (!conf.success) throw new Error(conf.error || 'Confirmation impossible');
      nav({ page: 'tickets' });
    } catch (e) {
      const msg = e?.error || e?.message || 'Erreur';
      setError(e?.taken ? `La place ${e.taken} vient d’être prise.` : msg);
      if (e?.taken) setSelected(l => l.filter(x => x.seat_code !== e.taken));
      load();
    } finally { setBusy(false); setPhase(null); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel">
          <div style={{ display: 'flex', gap: 12 }}>
            {s.movie?.poster && <img src={s.movie.poster} alt="" style={{ width: 70, height: 100, objectFit: 'cover', borderRadius: 10 }} />}
            <div>
              <h3 style={{ margin: 0 }}>{s.movie?.title}</h3>
              <p className="mk-note" style={{ margin: '4px 0' }}>📅 {fmtDT(s.starts_at)} · {s.format}</p>
              <p className="mk-note" style={{ margin: '4px 0' }}>🎬 {data.cinema?.business_name} · {s.screen_name} · {s.available} places disponibles</p>
            </div>
          </div>
          <h4 style={{ margin: '16px 0 6px' }}>Tarif des places</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tiers.map(x => <button key={x.code} className={`mk-cat${tier === x.code ? ' active' : ''}`} onClick={() => setTier(x.code)}><span>{x.label} · {money(x.price)}</span></button>)}
          </div>
          {numbered ? (
            <>
              <h4 style={{ margin: '16px 0 6px' }}>Choisissez vos places</h4>
              <SeatMap layout={s.layout} sold={data.seats?.sold || []} held={data.seats?.held || []} selected={selected.map(x => x.seat_code)} onToggle={toggle} />
              {selected.map(x => (
                <div key={x.seat_code} className="mk-line" style={{ alignItems: 'center' }}>
                  <div className="info"><div className="name">Place {x.seat_code}{seatType[x.seat_code] === 'vip' ? ' · VIP' : ''}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>{tiers.map(tr => <button key={tr.code} className={`mk-cat${x.tier_code === tr.code ? ' active' : ''}`} style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => setSelected(l => l.map(y => y.seat_code === x.seat_code ? { ...y, tier_code: tr.code } : y))}><span>{tr.label}</span></button>)}</div></div>
                  <div className="price">{money(unit(x.tier_code, x.seat_code))}</div>
                </div>
              ))}
            </>
          ) : (
            <>
              <h4 style={{ margin: '16px 0 6px' }}>Nombre de places</h4>
              {tiers.map(x => (
                <div key={x.code} className="mk-line" style={{ alignItems: 'center' }}>
                  <div className="info"><div className="name">{x.label} · {money(unit(x.code))}</div></div>
                  <div className="mk-qty"><button onClick={() => setQty(q => ({ ...q, [x.code]: Math.max(0, (q[x.code] || 0) - 1) }))}>−</button><b>{qty[x.code] || 0}</b><button onClick={() => setQty(q => (items.length >= maxSeats ? q : { ...q, [x.code]: (q[x.code] || 0) + 1 }))}>+</button></div>
                </div>
              ))}
            </>
          )}
          {(data.concession || []).length > 0 && (
            <>
              <h4 style={{ margin: '16px 0 6px' }}>🍿 Confiserie (retrait au comptoir)</h4>
              {data.concession.map(p => (
                <div key={p.product_id} className="mk-line" style={{ alignItems: 'center' }}>
                  {p.image ? <img src={p.image_thumb || p.image} alt="" /> : null}
                  <div className="info"><div className="name">{p.name}</div><div className="opt">{money(p.price)}</div></div>
                  <div className="mk-qty"><button onClick={() => setConc(c => ({ ...c, [p.product_id]: Math.max(0, (c[p.product_id] || 0) - 1) }))}>−</button><b>{conc[p.product_id] || 0}</b><button onClick={() => setConc(c => ({ ...c, [p.product_id]: Math.min(20, (c[p.product_id] || 0) + 1) }))}>+</button></div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="mk-panel">
          <h3>Paiement</h3>
          {needAge && (
            <label className="mk-opt" style={{ cursor: 'pointer' }}><input type="checkbox" checked={attested} onChange={e => setAttested(e.target.checked)} style={{ marginRight: 8 }} /> J’atteste que tous les spectateurs ont {s.movie.age_rating} ans ou plus</label>
          )}
          <div className="mk-pay">
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}><Wallet size={19} />Solde Mbiyo</button>
            <button className={pay === 'mobile_money' ? 'on' : ''} onClick={() => setPay('mobile_money')}><Smartphone size={19} />Mobile Money</button>
          </div>
          {pay === 'mobile_money' && (
            <div style={{ marginTop: 10 }}>
              <div className="mk-field"><label>Numéro Mobile Money</label><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" inputMode="tel" /></div>
              <div className="mk-field"><label>Opérateur</label><select value={telecom} onChange={e => setTelecom(e.target.value)}><option value="AM">Airtel Money</option><option value="MP">M-Pesa</option><option value="OM">Orange Money</option></select></div>
            </div>
          )}
          <div className="mk-total"><span>{items.length} place(s)</span><span>{money(items.reduce((a, it) => a + unit(it.tier_code, it.seat_code), 0))}</span></div>
          {concList.length > 0 && <div className="mk-total"><span>Confiserie</span><span>{money(concList.reduce((a, c) => a + c.unit * c.qty, 0))}</span></div>}
          <div className="mk-total grand"><span>Total</span><span>{money(total)}</span></div>
          {!currentUser && <p className="mk-note warn" style={{ marginTop: 8 }}>Connectez-vous pour réserver.</p>}
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy || !canBuy} onClick={buy}>
            {busy ? (phase || 'Réservation…') : !s.online_open ? 'Vente en ligne terminée' : `Réserver — ${money(total)}`}
          </button>
          <p className="mk-note" style={{ marginTop: 10 }}>Places bloquées {data.settings?.hold_minutes || 10} min pendant le paiement. Billet ni remboursable ni échangeable, sauf annulation de la séance par le cinéma.</p>
        </div>
      </div>
    </div>
  );
}

/** Bloc « billets cinéma » inséré dans la page Mes billets. */
export function CinemaTicketsBlock() {
  const [tickets, setTickets] = useState(null);
  useEffect(() => { apiService.get('/cinema/my/tickets').then(r => setTickets(r?.tickets || [])).catch(() => setTickets([])); }, []);
  if (!tickets?.length) return null;
  const seenOrders = new Set();
  return (
    <div className="mk-panel" style={{ marginTop: 16 }}>
      <h3>🎬 Cinéma</h3>
      {tickets.map(t => {
        const first = !seenOrders.has(t.order_id); seenOrders.add(t.order_id);
        const st = t.showtime || {}; const o = t.order;
        return (
          <div className="mk-line" key={t.ticket_id}>
            <div className="info">
              <div className="name">{st.movie?.title || 'Film'}</div>
              <div className="opt">{st.starts_at ? fmtDT(st.starts_at) : ''} · {t.cinema?.business_name || ''} · {st.screen_name}{t.seat_code ? ` · Place ${t.seat_code}` : ''} · {t.tier_label}</div>
              {first && o?.concession_code && <div className="opt" style={{ marginTop: 4 }}>🍿 {(o.concessions || []).map(c => `${c.qty}× ${c.name}`).join(', ')} — code de retrait : <b style={{ letterSpacing: 1 }}>{o.concession_code.slice(-8).toUpperCase()}</b> ({o.concession_status === 'collected' ? 'remise' : 'au comptoir, avant la séance ou à l’entracte'})</div>}
              {first && t.order_id && t.channel === 'online' && <InvoiceLink type="cinema" id={t.order_id} compact />}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 900, letterSpacing: 2, color: t.status === 'valid' ? 'var(--mb-fire)' : 'var(--mb-text-3)' }}>{t.public_id}</div>
              <span className={`mk-open ${t.status === 'valid' ? 'on' : 'off'}`}>{t.status === 'valid' ? 'Valide' : t.status === 'used' ? 'Utilisé' : 'Remboursé'}</span>
            </div>
          </div>
        );
      })}
      <p className="mk-note" style={{ marginTop: 10 }}>Présentez le numéro de billet à l’entrée — le contrôleur le vérifie dans son application. Les QR complets sont dans l’app Mbiyo.</p>
    </div>
  );
}
